import React, { useEffect, useState } from 'react';
import AuthenticatedApp from './components/authenticated/authenticated-app';
import UnauthenticatedApp from './components/unauthenticated/unauthenticated-app';
import createAppStateStore from './app-state-store';
import { cetraLightModeTheme } from './theme';
import { observer } from 'mobx-react-lite';
import AppAuthState from './react-core-lib/components/global/AuthState';
import ContentManager from './components/state-mgmt/content-manager';
import AuthenticatedAppWithStore from './react-core-lib/components/app/authenticated-app-with-store';
import configureAmplify from './react-core-lib/third-party/amplify';
import mixpanel from 'mixpanel-browser';
import ReactGA4 from 'react-ga4';
import './App.css';
import './console-overrides';
import { register } from 'swiper/element/bundle';
import AccessibilityWidget from './AccessibilityWidget';
import config from './config';
import {
    getClinikoPatientIdFromSearch,
    persistClinikoPatientId,
    markClinikoParamsSeen,
} from './components/helper/clinikoParamsHelper';

register();
configureAmplify();

function App() {
    useEffect(() => {
        const captureClinikoParams = () => {
            const patientId = getClinikoPatientIdFromSearch(window.location.search || '');
            if (patientId) {
                console.log('[Cliniko] patient_id detected, storing for current session.');
                persistClinikoPatientId(patientId);
                markClinikoParamsSeen();
            }
        };

        // Initial capture on mount
        captureClinikoParams();

        // Capture UTM parameters on initial page load (only if not already stored)
        const captureUtmParams = () => {
            const existingUtmParams = localStorage.getItem('utmParams');
            if (!existingUtmParams) {
                const urlParams = new URLSearchParams(window.location.search);
                const utmParams = {};
                ['utm_source', 'utm_medium', 'utm_campaign', 'utm_term', 'utm_content'].forEach((param) => {
                    const value = urlParams.get(param);
                    if (value) {
                        utmParams[param] = value;
                    }
                });
                if (Object.keys(utmParams).length > 0) {
                    localStorage.setItem('utmParams', JSON.stringify(utmParams));
                    console.log('[UTM] Captured UTM parameters:', utmParams);
                }
            }
        };
        captureUtmParams();

        const { clinicType, isProd, mixpanelToken, gtagId } = config;

        if (mixpanelToken) {
            mixpanel.init(mixpanelToken, {
                api_host: 'https://api-eu.mixpanel.com',
                verbose: true,
                debug: true,
                track_pageview: 'url-with-path',
                persistence: 'localStorage',
                ignore_dnt: true,
            });
            console.log(`Mixpanel initialized with token: ${mixpanelToken}`, clinicType, isProd);
        }

        if (gtagId) {
            ReactGA4.initialize(gtagId);
        }

        const handleLocationChange = () => {
            captureClinikoParams();
        };

        const handleVisibilityChange = () => {
            if (document.visibilityState === 'visible') {
                captureClinikoParams();
            }
        };

        const handleFocus = () => {
            captureClinikoParams();
        };

        // Add event listeners
        window.addEventListener('popstate', handleLocationChange);
        window.addEventListener('hashchange', handleLocationChange);
        document.addEventListener('visibilitychange', handleVisibilityChange);
        window.addEventListener('focus', handleFocus);

        // Cleanup
        return () => {
            window.removeEventListener('popstate', handleLocationChange);
            window.removeEventListener('hashchange', handleLocationChange);
            document.removeEventListener('visibilitychange', handleVisibilityChange);
            window.removeEventListener('focus', handleFocus);
        };
    }, []);

    return (
        <>
            <AuthenticatedAppWithStore
                AuthenticatedComponent={AuthenticatedApp}
                UnauthenticatedComponent={UnauthenticatedApp}
                createAppStateStore={createAppStateStore}
                theme={cetraLightModeTheme}
                globalComponents={
                    <>
                        <AppAuthState />
                        <AccessibilityWidget />
                    </>
                }
                globalAuthenticatedComponents={<ContentManager />}
            />
        </>
    );
}

export default observer(App);
